import { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import Cropper from 'react-easy-crop'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Chip,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  Edit,
  Save,
  CameraAlt,
  Email,
  Person,
  Business,
  Phone,
  LocationOn,
  Lock,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Badge as BadgeIcon,
  CheckCircle,
  CalendarToday,
  Close,
} from '@mui/icons-material'
import { format } from 'date-fns'

import { AppDispatch, RootState } from '../store'
import { updateProfile, changePassword, loadUser } from '../store/slices/authSlice'
import { getInitials } from '../utils/userColors'
import { getProfilePageRoleColor } from '../utils/statusColors'
import { uploadAvatar } from '../services/api'
import { getUserColor } from '../utils/userColors'
import LoadingSpinner from '../components/common/LoadingSpinner'

// Helper function to safely format dates
const safeFormatDate = (dateString: string | undefined | null, formatString: string, fallback: string = 'Not available'): string => {
  if (!dateString) return fallback
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return fallback
    return format(date, formatString)
  } catch (error) {
    return fallback
  }
}

interface ProfileFormData {
  name: string
  phone?: string
  location?: string
}

interface PasswordFormData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { user, loading } = useSelector((state: RootState) => state.auth)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Image crop state
  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.name || '',
      phone: user?.phone || '',
      location: user?.location || '',
    },
  })

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch: watchPasswordField,
  } = useForm<PasswordFormData>()

  // Update form when user data changes
  useEffect(() => {
    if (user && editDialogOpen) {
      resetProfile({
        name: user.name || '',
        phone: user.phone || '',
        location: user.location || '',
      })
    }
  }, [user, editDialogOpen, resetProfile])

  // Load user data on mount
  useEffect(() => {
    if (!user) {
      dispatch(loadUser())
    }
  }, [dispatch, user])

  const handleEditOpen = () => {
    setEditDialogOpen(true)
    setError(null)
    if (user) {
      resetProfile({
        name: user.name || '',
        phone: user.phone || '',
        location: user.location || '',
      })
    }
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setError(null)
  }

  const onSubmitProfile = async (data: ProfileFormData) => {
    try {
      setError(null)
      const result = await dispatch(updateProfile(data))
      if (updateProfile.fulfilled.match(result)) {
        setEditDialogOpen(false)
      } else {
        setError(result.payload as string || 'Failed to update profile')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile')
    }
  }

  const onSubmitPassword = async (data: PasswordFormData) => {
    try {
      setPasswordError(null)
      
      if (data.newPassword !== data.confirmPassword) {
        setPasswordError('New passwords do not match')
        return
      }

      const result = await dispatch(
        changePassword({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        })
      )

      if (changePassword.fulfilled.match(result)) {
        setIsChangingPassword(false)
        resetPassword()
      } else {
        setPasswordError(result.payload as string || 'Failed to change password')
      }
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password')
    }
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  // Helper function to create cropped image
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: { x: number; y: number; width: number; height: number }
  ): Promise<Blob> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('No 2d context')
    }

    // Set canvas size to 200x200
    canvas.width = 200
    canvas.height = 200

    // Draw and resize the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      200,
      200
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'))
            return
          }
          resolve(blob)
        },
        'image/jpeg',
        0.9 // Quality (0.9 = 90%)
      )
    })
  }

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleCropAndUpload = async () => {
    if (!imageToCrop || !croppedAreaPixels) return

    try {
      setUploadingAvatar(true)
      setError(null)

      // Create cropped image blob
      const croppedImageBlob = await getCroppedImg(imageToCrop, croppedAreaPixels)

      // Check if cropped image is under 200KB, if not, reduce quality
      let finalBlob = croppedImageBlob
      if (croppedImageBlob.size > 200 * 1024) {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = 200
        canvas.height = 200

        const image = await createImage(imageToCrop)
        ctx?.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          200,
          200
        )

        // Try different quality levels
        for (let quality = 0.8; quality >= 0.3; quality -= 0.1) {
          finalBlob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) reject(new Error('Canvas is empty'))
                else resolve(blob)
              },
              'image/jpeg',
              quality
            )
          })
          if (finalBlob.size <= 200 * 1024) break
        }
      }

      // Convert blob to File
      const croppedFile = new File([finalBlob], 'avatar.jpg', { type: 'image/jpeg' })

      // Upload avatar
      await uploadAvatar(croppedFile)

      // Update user in store
      await dispatch(loadUser())
      setCropDialogOpen(false)
      setImageToCrop(null)
      setAvatarPreview(null)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload avatar')
    } finally {
      setUploadingAvatar(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setError('Only image files are allowed (PNG, JPG, GIF, WebP)')
      return
    }

    // Validate file size (5MB initial limit - will be compressed after cropping)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB')
      return
    }

    // Create preview and open crop dialog
    const reader = new FileReader()
    reader.onloadend = () => {
      const imageUrl = reader.result as string
      setImageToCrop(imageUrl)
      setCropDialogOpen(true)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
    }
    reader.readAsDataURL(file)
  }

  const getRoleColor = getProfilePageRoleColor

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings fontSize="small" />
      default:
        return <BadgeIcon fontSize="small" />
    }
  }

  if (loading && !user) {
    return <LoadingSpinner message="Loading profile..." />
  }

  if (!user) {
    return (
      <Box>
        <Alert severity="error">Failed to load user data</Alert>
      </Box>
    )
  }

  const displayAvatar = avatarPreview || user.avatar
  const avatarInitial = getInitials(user.name, 'U')
  const avatarColor = getUserColor(user._id)

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    color: '#1e293b',
                  }}
                >
                  Profile Information
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleEditOpen}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#cbd5e1',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      backgroundColor: '#f1f5f9',
                    },
                  }}
                >
                  Edit Profile
                </Button>
              </Box>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2, borderRadius: 1.5 }} 
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              {/* User Info Display */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' }, alignItems: 'flex-start' }}>
                  {/* Avatar */}
                  <Box sx={{ position: 'relative', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={displayAvatar || undefined}
                        sx={{
                          width: 120,
                          height: 120,
                          bgcolor: displayAvatar ? undefined : avatarColor,
                          fontSize: '3rem',
                          cursor: 'pointer',
                          border: '4px solid',
                          borderColor: '#3b82f6',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)',
                          },
                        }}
                        onClick={handleAvatarClick}
                      >
                        {!displayAvatar && avatarInitial}
                      </Avatar>
                      {uploadingAvatar && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            borderRadius: '50%',
                          }}
                        >
                          <LoadingSpinner message="" />
                        </Box>
                      )}
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          bgcolor: '#3b82f6',
                          color: 'white',
                          border: '3px solid white',
                          width: 36,
                          height: 36,
                          '&:hover': { 
                            bgcolor: '#2563eb',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={handleAvatarClick}
                        disabled={uploadingAvatar}
                      >
                        <CameraAlt fontSize="small" />
                      </IconButton>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleAvatarChange}
                      />
                    </Box>
                    <Box sx={{ mt: 1.5, textAlign: 'center', maxWidth: 140 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block' }}>
                        Click to upload a new avatar
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ color: '#94a3b8', fontSize: '0.65rem', display: 'block', mt: 0.25 }}>
                        PNG, JPG, GIF or WebP. Max size 200KB. Image will be cropped to 200x200px
                      </Typography>
                    </Box>
                  </Box>

                  {/* User Details */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Stack spacing={2.5}>
                      {/* Name */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                          <Person sx={{ color: '#f59e0b', fontSize: '1.25rem' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.25 }}>
                            Name
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.4 }}>
                            {user.name}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Email */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                          <Email sx={{ color: '#3b82f6', fontSize: '1.25rem' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.25 }}>
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.4, wordBreak: 'break-word' }}>
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Department */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                          <Business sx={{ color: '#10b981', fontSize: '1.25rem' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.25 }}>
                            Department
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.4 }}>
                            {user.department || 'Not specified'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Phone */}
                      {user.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                            <Phone sx={{ color: '#a855f7', fontSize: '1.25rem' }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.25 }}>
                              Phone
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.4 }}>
                              {user.phone}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Location */}
                      {user.location && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                            <LocationOn sx={{ color: '#f97316', fontSize: '1.25rem' }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.25 }}>
                              Location
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.4 }}>
                              {user.location}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
              border: '1px solid #e0e6ed',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#1e293b',
                }}
              >
                Account Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />

              <Stack spacing={1.5}>
                {/* Role */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${getRoleColor(user.role)}15 0%, ${getRoleColor(user.role)}08 100%)`,
                    border: `1px solid ${getRoleColor(user.role)}30`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: `0 2px 8px ${getRoleColor(user.role)}20`,
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: getRoleColor(user.role) }}>
                      {getRoleIcon(user.role)}
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#64748b',
                        fontSize: '0.75rem',
                      }}
                    >
                      Role
                    </Typography>
                  </Box>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{
                      textTransform: 'capitalize',
                      backgroundColor: getRoleColor(user.role),
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                </Box>

                {/* Last Login */}
                {user.lastLogin && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #dbeafe15 0%, #dbeafe08 100%)',
                      border: '1px solid #3b82f630',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 2px 8px #3b82f620',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday sx={{ color: '#3b82f6', fontSize: '1.1rem' }} />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 600, 
                          color: '#64748b',
                          fontSize: '0.75rem',
                        }}
                      >
                        Last Login
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: '#1e293b',
                        fontSize: '0.75rem',
                      }}
                    >
                      {safeFormatDate(user.lastLogin, 'MMM dd, yyyy')}
                    </Typography>
                  </Box>
                )}

                {/* Member Since */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #ecfdf515 0%, #ecfdf508 100%)',
                    border: '1px solid #10b98130',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 2px 8px #10b98120',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ color: '#10b981', fontSize: '1.1rem' }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#64748b',
                        fontSize: '0.75rem',
                      }}
                    >
                      Member Since
                    </Typography>
                  </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: '#1e293b',
                        fontSize: '0.75rem',
                      }}
                    >
                      {safeFormatDate(user.createdAt, 'MMM dd, yyyy', 'Not available')}
                    </Typography>
                </Box>

                {/* Account Status */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: 1.5,
                    background: (user.isActive !== false)
                      ? 'linear-gradient(135deg, #ecfdf515 0%, #ecfdf508 100%)'
                      : 'linear-gradient(135deg, #fef2f215 0%, #fef2f208 100%)',
                    border: (user.isActive !== false)
                      ? '1px solid #10b98130'
                      : '1px solid #ef444430',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: (user.isActive !== false)
                        ? '0 2px 8px #10b98120'
                        : '0 2px 8px #ef444420',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ 
                      color: (user.isActive !== false) ? '#10b981' : '#ef4444', 
                      fontSize: '1.1rem' 
                    }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#64748b',
                        fontSize: '0.75rem',
                      }}
                    >
                      Status
                    </Typography>
                  </Box>
                  <Chip
                    label={(user.isActive !== false) ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      backgroundColor: (user.isActive !== false) ? '#10b981' : '#ef4444',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card
            sx={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
              border: '1px solid #e0e6ed',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Lock sx={{ color: '#8b5cf6', fontSize: '1.2rem' }} />
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: '#1e293b',
                    }}
                  >
                    Change Password
      </Typography>
                </Box>
                {!isChangingPassword && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => {
                      setIsChangingPassword(true)
                      setPasswordError(null)
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: '#cbd5e1',
                      color: '#475569',
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: '#94a3b8',
                        backgroundColor: '#f1f5f9',
                      },
                    }}
                  >
                    Change
                  </Button>
                )}
              </Box>

              {passwordError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2, borderRadius: 1.5 }} 
                  onClose={() => setPasswordError(null)}
                >
                  {passwordError}
                </Alert>
              )}

              {isChangingPassword && (
                <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Current Password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      {...registerPassword('currentPassword', {
                        required: 'Current password is required',
                      })}
                      error={!!passwordErrors.currentPassword}
                      helperText={passwordErrors.currentPassword?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: '#8b5cf6', fontSize: '1rem' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                              size="small"
                            >
                              {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="New Password"
                      type={showNewPassword ? 'text' : 'password'}
                      {...registerPassword('newPassword', {
                        required: 'New password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' },
                      })}
                      error={!!passwordErrors.newPassword}
                      helperText={passwordErrors.newPassword?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: '#8b5cf6', fontSize: '1rem' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              size="small"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...registerPassword('confirmPassword', {
                        required: 'Please confirm your new password',
                        validate: (value) =>
                          value === watchPasswordField('newPassword') || 'Passwords do not match',
                      })}
                      error={!!passwordErrors.confirmPassword}
                      helperText={passwordErrors.confirmPassword?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: '#8b5cf6', fontSize: '1rem' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setIsChangingPassword(false)
                          resetPassword()
                          setPasswordError(null)
                        }}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: '#cbd5e1',
                          color: '#475569',
                          '&:hover': {
                            borderColor: '#94a3b8',
                            backgroundColor: '#f1f5f9',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={loading}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          backgroundColor: '#8b5cf6',
                          '&:hover': {
                            backgroundColor: '#7c3aed',
                          },
                        }}
                      >
                        Update
                      </LoadingButton>
                    </Box>
                  </Box>
                </form>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Profile
      </Typography>
            <IconButton onClick={handleEditClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
          <DialogContent dividers>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Stack spacing={2.5}>
              {/* Name */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #fef3c715 0%, #fef3c708 100%)',
                  border: '1px solid #fbbf2430',
                }}
              >
                <TextField
                  fullWidth
                  label="Name"
                  {...registerProfile('name', {
                    required: 'Name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                    maxLength: { value: 50, message: 'Name cannot exceed 50 characters' },
                  })}
                  error={!!profileErrors.name}
                  helperText={profileErrors.name?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#f59e0b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Box>

              {/* Phone */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #f3e8ff15 0%, #f3e8ff08 100%)',
                  border: '1px solid #a855f730',
                }}
              >
                <TextField
                  fullWidth
                  label="Phone Number"
                  {...registerProfile('phone', {
                    maxLength: { value: 20, message: 'Phone cannot exceed 20 characters' },
                  })}
                  error={!!profileErrors.phone}
                  helperText={profileErrors.phone?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: '#a855f7' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Box>

              {/* Location */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #fff7ed15 0%, #fff7ed08 100%)',
                  border: '1px solid #f9731630',
                }}
              >
                <TextField
                  fullWidth
                  label="Location"
                  {...registerProfile('location', {
                    maxLength: { value: 100, message: 'Location cannot exceed 100 characters' },
                  })}
                  error={!!profileErrors.location}
                  helperText={profileErrors.location?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: '#f97316' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleEditClose}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#cbd5e1',
                color: '#475569',
                '&:hover': {
                  borderColor: '#94a3b8',
                  backgroundColor: '#f1f5f9',
                },
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              startIcon={<Save />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#3b82f6',
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
              }}
            >
              Save Changes
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Image Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={() => {
          setCropDialogOpen(false)
          setImageToCrop(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Crop Your Avatar
            </Typography>
            <IconButton 
              onClick={() => {
                setCropDialogOpen(false)
                setImageToCrop(null)
                if (fileInputRef.current) {
                  fileInputRef.current.value = ''
                }
              }} 
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Adjust and crop your image. The final image will be 200x200 pixels and must be under 200KB.
            </Typography>
            {imageToCrop && (
              <Box
                sx={{
                  position: 'relative',
                  height: 300,
                  width: '100%',
                  bgcolor: '#000',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                />
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Zoom
              </Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(_e, value) => setZoom(value as number)}
                sx={{ width: '100%' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setCropDialogOpen(false)
              setImageToCrop(null)
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f1f5f9',
              },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleCropAndUpload}
            loading={uploadingAvatar}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#3b82f6',
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Upload Avatar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProfilePage
